import React, { useEffect } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { chatImageUrl, prettySize } from '../../helpers/chatMedia';
import { dayLabel, messageTime } from '../../helpers/chatTime';
import './Chat.scss';

/* A shared picture at full size.

   Rendered where it sits in the tree rather than through a portal: the dock's
   windows and the chat page both already establish their own stacking, and a
   fixed overlay at a high enough layer covers the page from either without
   needing one. */
const ImageLightbox = ({ message, onClose }) => {
    const attachment = message && message.attachment;

    /* Escape closes it, and the page behind is locked so a scroll gesture
       meant for the picture does not run the thread away underneath it. */
    useEffect(() => {
        if (!attachment) return undefined;
        const escape = (event) => { if (event.key === 'Escape') onClose(); };
        const { overflow } = document.body.style;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', escape);
        return () => {
            document.body.style.overflow = overflow;
            document.removeEventListener('keydown', escape);
        };
    }, [attachment, onClose]);

    if (!attachment) return null;

    /* The stored copy wherever there is one — the blob only stands in while a
       picture the sender just chose is still on its way up. */
    const src = attachment.file ? chatImageUrl(attachment.file) : message.previewUrl;

    return (
        <div className="chat-lightbox" role="dialog" aria-modal="true" onClick={onClose}>
            <div className="chat-lightbox-bar" onClick={(event) => event.stopPropagation()}>
                <span className="chat-lightbox-name">
                    <b>{attachment.name || 'Image'}</b>
                    <em>
                        {dayLabel(message.createdAt)} · {messageTime(message.createdAt)}
                        {attachment.size ? ` · ${prettySize(attachment.size)}` : ''}
                    </em>
                </span>
                <a
                    className="chat-lightbox-tool"
                    href={src}
                    download={attachment.name || attachment.file}
                    title="Download"
                >
                    <DownloadRoundedIcon />
                </a>
                <button type="button" className="chat-lightbox-tool" onClick={onClose} title="Close">
                    <CloseRoundedIcon />
                </button>
            </div>

            {/* The click that closes the overlay must not also fire when the
                picture itself is clicked, or it would be impossible to look
                at anything without dismissing it. */}
            <img
                src={src}
                alt={attachment.name || 'Shared image'}
                onClick={(event) => event.stopPropagation()}
            />
        </div>
    );
};

export default ImageLightbox;
